"use client";

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { Loader2 } from "lucide-react";
import { Button } from "@mozaic/ui/components/button";
import { Skeleton } from "@mozaic/ui/components/skeleton";
import { queryKeys } from "@/shared/api/keys";
import { beginSso, isNavigableUrl, listIdentityProviders } from "./api";

export function SsoProviders() {
  const t = useTranslations("auth");
  const providers = useQuery({ queryKey: queryKeys.identityProviders, queryFn: listIdentityProviders, retry: 0 });
  const [starting, setStarting] = useState<string | null>(null);
  const [error, setError] = useState(false);

  async function start(name: string) {
    setStarting(name);
    setError(false);
    try {
      const url = await beginSso(name);
      if (!isNavigableUrl(url)) throw new Error("unsafe url");
      window.location.assign(url);
    } catch {
      setError(true);
      setStarting(null);
    }
  }

  if (providers.isPending) return <Skeleton className="h-10 w-full" />;
  if (providers.isError) {
    return (
      <p role="status" className="text-center text-xs text-muted-foreground">
        {t("sso.unavailable")}
      </p>
    );
  }
  if (providers.data.length === 0) return null;
  return (
    <section aria-labelledby="sso-heading" className="space-y-3">
      <h2 id="sso-heading" className="text-center text-xs uppercase tracking-wide text-muted-foreground">
        {t("sso.heading")}
      </h2>
      {error && (
        <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
          {t("sso.beginFailed")}
        </p>
      )}
      <ul className="space-y-2">
        {providers.data.map((provider) => (
          <li key={provider.id}>
            <Button variant="outline" className="w-full" disabled={starting !== null} onClick={() => void start(provider.id)}>
              {starting === provider.id && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
              {t("sso.continueWith", { name: provider.name })}
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}
